<script lang="ts">
  import { Pages } from '@packages/utils/enums'
  import { onDestroy } from 'svelte'
  import { get } from 'svelte/store'
  import { ethers } from '@credenza3/core-web-evm-ext'

  import { pageOptsStore, userStore, pageStore, fn, clientPublicStore } from '@packages/stores'
  import { DEFAULT_TOKEN_ID } from '@packages/utils/constants'
  import { dispatch, PassportEvents } from '@lib/events/events'
  import type { TPaymentItem } from '@packages/utils/types'
  import { Payment } from '@packages/ui'
  import Stripe from './stripe/Stripe.svelte'
  import CredenzaBalance from './credenza-balance/CredenzaBalance.svelte'
  import { configStore, providerStore, contractsStore } from '@src/stores'
  import { abi as content_abi } from '@credenza3/contracts/artifacts/ContentNftContract.json'
  import Loader from '@packages/ui/main/shared/Loader.svelte'

  const { close } = get(fn)
  const chainId = get(configStore).chainId

  $: if ($configStore.chainId !== chainId) close()

  let totalSumCents: number = 0
  let totalSumToken: number = 0
  let isPriceLoading: boolean = false
  let isLoading: boolean = false
  let feeToApply: number = 0

  const clientCredFee = get(clientPublicStore).client_payment_configuration?.token_fee

  let pricesByToken: { [key: string]: number } = {}

  const getTokenPrice = async (
    contractAddress: string,
    tokenId: bigint | string,
    licenseType?: string,
  ): Promise<[number, number]> => {
    let priceCents = 0
    let priceToken = 0

    if (licenseType) {
      const contract = new ethers.Contract(contractAddress, content_abi, $providerStore)
      priceCents = await contract
        .getLicensePriceFiat(String(tokenId), licenseType)
        .then((result: bigint) => Number(result))
      priceToken = await contract
        .getLicensePriceToken(String(tokenId), licenseType)
        .then((result: bigint) => Number(result))
      return [priceCents, priceToken]
    }

    const { credenzaSellableMinAbi } = await import('@src/lib/abi')
    const sellableContract = new ethers.Contract(contractAddress, credenzaSellableMinAbi, $providerStore)
    priceCents = await sellableContract.getPriceFiat(tokenId).then((result: bigint) => Number(result))
    priceToken = 0

    if (priceCents === 0) {
      ;[priceCents, priceToken] = await Promise.all([
        sellableContract.getPriceFiat(DEFAULT_TOKEN_ID).then((result: bigint) => Number(result)),
        sellableContract.getPriceToken(DEFAULT_TOKEN_ID).then((result: bigint) => Number(result)),
      ])
    } else {
      priceToken = await sellableContract.getPriceToken(tokenId).then((result: bigint) => Number(result))
    }

    return [priceCents, priceToken]
  }

  const calculateTotalPrice = async (items: TPaymentItem[]): Promise<void> => {
    try {
      isPriceLoading = true
      for (const token of items) {
        if (token.contractAddress === $contractsStore.erc20Cred.address) {
          totalSumCents += token.amount * 100

          if (clientCredFee) {
            feeToApply = clientCredFee
            totalSumCents += totalSumCents * clientCredFee
          }
        } else {
          const membershipTokenId = token.membershipTokenId || DEFAULT_TOKEN_ID
          const tokenId = token.tokenId || (token.contentTokenId as string) || membershipTokenId

          let [priceCents, priceToken] = await getTokenPrice(token.contractAddress, tokenId, token.licenseType)

          if (token?.amount && token?.amount !== 0) {
            priceCents *= token.amount
            priceToken *= token.amount
          }

          totalSumCents += priceCents
          totalSumToken += priceToken

          pricesByToken[`${token.contractAddress}/${tokenId}`] = priceToken
        }
      }
    } catch (err) {
      dispatch(PassportEvents.ERROR, err)
    } finally {
      isPriceLoading = false
    }
  }

  const unsub = pageOptsStore.subscribe(async (store) => {
    if (!store?.tokens?.length && !store?.memberships?.length && !store?.licenses?.length) return

    await calculateTotalPrice([...(store?.tokens || []), ...(store?.memberships || []), ...(store?.licenses || [])])
  })

  onDestroy(unsub)
</script>

{#if isLoading}
  {#await import('@components/progress/ProgressVideo.svelte') then module}
    <svelte:component this={module.default} />
  {/await}
{/if}

<Payment
  bind:isLoading
  options={{
    user: $userStore,
    title: $pageOptsStore.title || `Order Summary`,
    subtitle: $pageOptsStore.subtitle,
    onBackClick: () => pageStore.set(Pages.WALLET),
    isCardDisabled: $pageOptsStore.payments?.card?.disabled,
    isCredezaValueDisabled: !totalSumCents || $pageOptsStore.payments?.credenzaStoredValue?.disabled,
  }}
>
  <div slot="credit-card">
    {#if isPriceLoading}
      <Loader />
    {:else}
      <Stripe priceCents={totalSumCents} {isPriceLoading} bind:isLoading {feeToApply} />
    {/if}
  </div>
  <div slot="cred-balance">
    <CredenzaBalance {pricesByToken} totalPriceToken={totalSumToken} {isPriceLoading} bind:isLoading />
  </div>
</Payment>
